import axios, {
  AxiosRequestTransformer,
  AxiosResponse,
} from 'axios';
import { message } from 'antd';
import constants from 'src/constants/index';
import { BusinessError } from 'src/utils/business-error';
import { h } from 'src/utils/h';
import i18n from 'i18next';
// import { openConfirmModal } from './helper';
import { clearAllLS, getItem, getTokenData, setItem, setTokenData } from './localStorage';
import { countDownLogout, countDownRefreshToken } from './helper';
import { login } from 'src/api/users';

// const dateTransformer: AxiosRequestTransformer = data => {
//   if (data instanceof Date) {
//     // do your specific formatting here
//     console.log(data)
//     console.log(data.toLocaleString())
//     return data.toLocaleString();
//   } else if (Array.isArray(data)) {
//     return data.map(val => dateTransformer(val));
//   } else if (typeof data === 'object' && data !== null) {
//     return Object.fromEntries(
//       Object.entries(data).map(([key, val]) => [
//         key,
//         dateTransformer(val),
//       ]),
//     );
//   } else return data;
// };

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

const service = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // url = base url + request url
  timeout: 180000,
  cancelToken: source.token,
  // transformRequest: axios.defaults.transformRequest
  //   ? [dateTransformer].concat(
  //       axios.defaults.transformRequest,
  //     )
  //   : dateTransformer,
  // withCredentials: true // send cookies when cross-domain requests
});

let refreshTokenPromise: any = null;
let isRefreshing = false;
let refreshSubscribers: any = [];

function onRefreshed(token: any) {
  refreshSubscribers.forEach((callback: any) => callback(token));
}

function addRefreshSubscriber(callback: any) {
  refreshSubscribers.push(callback);
}

export const refreshSSOV2 = () => {
  let tenantCode = "";
  if (window.location.hostname) {
    tenantCode = window.location.hostname.split('.')[0];
  }
  let client_id = encodeURIComponent(constants.SSO_CLIENT_ID ?? '');
  const tokenData = JSON.parse(getTokenData());
  let refreshToken = tokenData.refresh_token;
  const refreshTokenAPIUrl = constants.SSO_URL_REFRESHTOKEN;

  return axios.get(
    `${refreshTokenAPIUrl}?tenantCode=${tenantCode}&clientId=${client_id}&refreshToken=${refreshToken}`,
    { withCredentials: true },
  );
};

// Request interceptors
service.interceptors.request.use(
  config => {
    const tokenData: any = JSON.parse(getTokenData());
    const sessionId = encodeURIComponent(
      getItem(constants.SSO_KEY_SESSION_ID) ?? '',
    );
    const eworkTokenData = encodeURIComponent(
      localStorage.getItem('eworkTokenData') ?? '',
    );
    if (tokenData !== null) {
      if (config.headers) {
        config.headers['eworkTokenData'] = eworkTokenData;
        config.headers['Authorization'] = 'Bearer ' + tokenData.id_token;
        config.headers['SessionId'] = tokenData.sessionId;
        config.headers['X-Frame-Options'] = 'DENY';
        config.headers['content-type'] = 'application/json'
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// let cancel: any = null;
// const CancelToken = axios.CancelToken;
// axios.defaults.cancelToken = new CancelToken((c) => {
//   cancel = c;
// });

// Response interceptors
service.interceptors.response.use(
  (response) => {
    const res = response.data
    const responseStatus = response.status
    if (responseStatus !== constants.AXIOS.HTTP_STATUS.SUCCESS) {
      //throw
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return response;
    }
  },
  async error => {
    const originalRequest = error.config;
    if (error.response) {
      const error_ = error.response as AxiosResponse;
      if (
        error_.status ===
        constants.AXIOS.HTTP_STATUS.BAD_REQUEST &&
        error_.data &&
        error_.data.message === 'businessError'
      ) {
        const businessErrors: BusinessError[] = JSON.parse(
          error_.data.errors,
        );
        if (businessErrors && businessErrors.length) {
          h.each(businessErrors, businessError => {
            message.error(
              i18n
                .t(`error.${businessError.errorCode}`)
                .toString(),
              constants.MESSAGE_DURATION,
            );
          });
          return Promise.reject(error);
        }
      }
      else if (
        error_.status ===
        constants.AXIOS.HTTP_STATUS.UNAUTHORIZED
        && error_.config.url !== '/auth/token-v2'
      ) {
        return Promise.reject(error)
      }
    }
    return Promise.reject(error);
  },
);

service.interceptors.response.use(
  (response) => {
    const res = response.data
    const responseStatus = response.status
    if (responseStatus !== constants.AXIOS.HTTP_STATUS.SUCCESS) {
      //throw
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return response
    }
  },
  async error => {
    const originalRequest = error.config;
    const error_ = error.response as AxiosResponse;
    if (
      error?.response?.data?.error === 'INVALID_TOKEN' ||
      error?.response?.data?.error === 'EXPIRED_TOKEN' ||
      error?.response?.data?.error === 'authenticate.jwtExpired' ||
      (error_.status === constants.AXIOS.HTTP_STATUS.UNAUTHORIZED && error_.config.url === '/auth/token-v2')
      && !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If a refresh token request is already in progress, queue the request
        return new Promise(resolve => {
          addRefreshSubscriber((token: any) => {
            originalRequest._retry = true;
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            resolve(service(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshSSOV2();
        if (response.status === 200) {
          const tokenData = JSON.parse(getTokenData());
          let token = response.data?.id_token;
          const otpToken = encodeURIComponent(
            getItem('otpToken') ?? '',
          );
          tokenData.id_token = response.data?.id_token;
          tokenData.refresh_token = response.data?.refresh_token;
          setItem('tokenData', JSON.stringify(tokenData));
          localStorage.removeItem('ework_sso_token');
          localStorage.setItem('ework_sso_token', response.data?.id_token);
          localStorage.setItem('ework_sso_refresh_token', response.data?.refresh_token);

          if (!token) {
            token = localStorage.getItem('ework_sso_token');
          }
          const addToken = 'Bearer ' + token;
          service.defaults.headers.common['Authorization'] = addToken;
          originalRequest.headers['Authorization'] = addToken;

          // Update eworkTokenData
          await login({ ssotoken: token, appVersion: 1, otpToken: otpToken }).then(
            (res: any) => {
              service.defaults.headers.common['eworkTokenData'] = res.data?.token;
              originalRequest.headers['eworkTokenData'] = res.data?.token;
              localStorage.setItem('eworkTokenData', res.data?.token);
            }
          );

          // Notify all subscribers with the new token
          onRefreshed(addToken);

          isRefreshing = false;
          return service(originalRequest);
        } else {
          source.cancel();
          countDownLogout();
        }
      } catch (err) {
        isRefreshing = false;
        source.cancel();
        countDownLogout();
        return Promise.reject(err);
      } finally {
        refreshSubscribers = [];
        isRefreshing = false;
      }

    } else {
      return Promise.reject(error);
    }
  },
);

export default service;